import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import anime from 'animejs';
import { FaMicrochip } from 'react-icons/fa';
import './About.css';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const statsRef = useRef(null);

  useEffect(() => {
    if (inView) {
      // Animate text
      anime({
        targets: '.about-text h2',
        translateX: [-100, 0],
        opacity: [0, 1],
        easing: 'easeOutExpo',
        duration: 1200
      });

      anime({
        targets: '.about-text p',
        translateY: [50, 0],
        opacity: [0, 1],
        easing: 'easeOutExpo',
        duration: 1000,
        delay: anime.stagger(200, { start: 300 })
      });

      // Animate stats with counter
      anime({
        targets: '.stat',
        scale: [0, 1],
        opacity: [0, 1],
        easing: 'easeOutElastic(1, .8)',
        duration: 1500,
        delay: anime.stagger(150, { start: 600 })
      });

      // Number counter animation
      const stats = document.querySelectorAll('.stat h3');
      stats.forEach((stat) => {
        const text = stat.textContent;
        const hasPlus = text.includes('+');
        const number = parseInt(text.replace('+', '')) || 0;
        
        if (number > 0) {
          anime({
            targets: stat,
            innerHTML: [0, number],
            easing: 'easeOutExpo',
            duration: 2000,
            delay: 800,
            round: 1,
            update: function(anim) {
              stat.innerHTML = Math.round(anim.animations[0].currentValue) + (hasPlus ? '+' : '');
            }
          });
        }
      });

      // Animate orbits
      anime({
        targets: '.orbit',
        scale: [0, 1],
        opacity: [0, 1],
        easing: 'easeOutExpo',
        duration: 1500,
        delay: anime.stagger(200, { start: 1000 })
      });
    }
  }, [inView]);

  const stats = [
    { value: '500+', label: 'Projects Completed' },
    { value: '200+', label: 'Happy Clients' },
    { value: '24/7', label: 'Support Available' }
  ];

  const textVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: 'easeOut' }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, x: 50, rotateY: -30 },
    visible: {
      opacity: 1,
      x: 0,
      rotateY: 0,
      transition: { duration: 0.8, ease: 'easeOut' }
    }
  };

  return (
    <section id="about" className="section about" ref={ref}>
      <div className="container">
        <div className="about-content">
          <motion.div
            className="about-text"
            variants={textVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            <h2>About <span className="gradient-text">Avionverse</span></h2>
            <p>
              We are pioneers in AI automation, transforming businesses worldwide through intelligent 
              technology solutions. Founded by a team of passionate AI engineers and data scientists, 
              Avionverse is on a mission to make advanced AI accessible to businesses of all sizes.
            </p>
            <p>
              Our expertise spans across machine learning, natural language processing, computer vision, 
              , process automation, agentic AI and generative AI. We've helped over 50+ companies automate their workflows, 
              and accelerate their digital transformation journey.
            </p>
            <p>
              What sets us apart is our commitment to delivering not just technology, but complete 
              solutions. From initial consultation to deployment and ongoing optimization, we partner 
              with you every step of the way to ensure measurable ROI and sustainable growth.
            </p>

            <div className="about-stats" ref={statsRef}>
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="stat glass"
                  whileHover={{ scale: 1.05, y: -5 }}
                >
                  <h3 className="gradient-text">{stat.value}</h3>
                  <p>{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="about-visual"
            variants={imageVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            style={{ perspective: 1000 }}
          >
            <motion.div
              className="rotating-cube"
              animate={{
                rotateY: [0, 360],
                rotateX: [0, 15, 0, -15, 0]
              }}
              transition={{
                rotateY: { duration: 20, repeat: Infinity, ease: 'linear' },
                rotateX: { duration: 5, repeat: Infinity, ease: 'easeInOut' }
              }}
            >
              <div className="visual-icon">
                <FaMicrochip />
              </div>
              <div className="orbit orbit-1">
                <div className="orbit-dot"></div>
              </div>
              <div className="orbit orbit-2">
                <div className="orbit-dot"></div>
              </div>
              <div className="orbit orbit-3">
                <div className="orbit-dot"></div>
              </div>
              
              {/* Energy waves */}
              <div className="energy-wave wave-1"></div>
              <div className="energy-wave wave-2"></div>
              <div className="energy-wave wave-3"></div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
